import React from 'react';
import { ClipboardList, Target, Crosshair, Users2, CalendarRange } from 'lucide-react';
import {
  SectionCard,
  SaveBar,
  Field,
  StringListEditor,
  ObjectListEditor,
  TextArea,
  inputClass,
  useDraft,
} from './shared';
import { phase1Data } from '../../data/phase1Data';

const GOVERNANCE_AREAS = [
  'Board structure & composition',
  'Strategic oversight',
  'Risk governance',
  'Financial controls & reporting',
  'Regulatory & legal compliance',
  'Stakeholder accountability',
  'Information governance',
  'Governance culture & behaviour',
];

const TextArea_ = TextArea;

const normalize = (planning) => ({
  objectives: planning?.objectives || [],
  scope_in: planning?.scope_in || '',
  scope_out: planning?.scope_out || '',
  time_period: planning?.time_period || '',
  governance_areas: planning?.governance_areas || [],
  stakeholders: planning?.stakeholders || [],
  notes: planning?.notes || '',
});

const PlanningTab = ({ sections, onSave, saving }) => {
  const initial = normalize(sections?.planning);
  const [draft, setDraft, dirty] = useDraft(initial);
  const set = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const toggleArea = (area) => {
    const has = draft.governance_areas.includes(area);
    set('governance_areas', has ? draft.governance_areas.filter((a) => a !== area) : [...draft.governance_areas, area]);
  };

  const stakeholderRef =
    (phase1Data?.sections || []).find((s) => s.id === 'stakeholder-mapping')?.stakeholderCategories || [];

  return (
    <div>
      <SaveBar onSave={() => onSave({ planning: draft })} saving={saving} dirty={dirty} label="Save Planning" />

      <SectionCard
        title="Review Objectives"
        icon={Target}
        subtitle="3–5 specific, measurable objectives"
      >
        <p className="text-sm font-medium text-ink/60 mb-3">{phase1Data?.objective}</p>
        <StringListEditor
          items={draft.objectives}
          onChange={(v) => set('objectives', v)}
          placeholder="e.g. Assess board effectiveness and oversight quality"
          addLabel="Add Objective"
        />
      </SectionCard>

      <SectionCard title="Scope Definition" icon={Crosshair}>
        <Field label="In Scope" hint="What this review will cover.">
          <TextArea_
            rows={3}
            value={draft.scope_in}
            onChange={(e) => set('scope_in', e.target.value)}
            placeholder="Areas, committees, and time period included in the review..."
          />
        </Field>
        <Field label="Out of Scope" hint="What is explicitly excluded.">
          <TextArea_
            rows={2}
            value={draft.scope_out}
            onChange={(e) => set('scope_out', e.target.value)}
            placeholder="Areas excluded to keep the review focused and realistic..."
          />
        </Field>
        <Field label="Time Period Reviewed">
          <input
            value={draft.time_period}
            onChange={(e) => set('time_period', e.target.value)}
            placeholder="e.g. Last 12–18 months"
            className={inputClass}
          />
        </Field>
        <Field label="Governance Areas In Scope">
          <div className="grid sm:grid-cols-2 gap-2">
            {GOVERNANCE_AREAS.map((area) => {
              const checked = draft.governance_areas.includes(area);
              return (
                <button
                  key={area}
                  type="button"
                  onClick={() => toggleArea(area)}
                  className={`text-left px-3 py-2 border border-ink/10 rounded-lg font-bold text-sm uppercase tracking-wide transition-all ${
                    checked ? 'bg-lime shadow-saas-sm' : 'bg-white'
                  }`}
                >
                  <span className="mr-2">{checked ? '☑' : '☐'}</span>
                  {area}
                </button>
              );
            })}
          </div>
        </Field>
      </SectionCard>

      <SectionCard
        title="Stakeholder Map"
        icon={Users2}
        subtitle="Who will be interviewed or consulted"
      >
        {stakeholderRef.length > 0 && (
          <div className="bg-cream border border-ink/10 rounded-lg p-3 mb-4">
            <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-2">
              Reference — typical governance actors
            </div>
            <div className="flex flex-wrap gap-1.5">
              {stakeholderRef.map((s) => (
                <span key={s.role} className="text-xs font-bold px-2 py-0.5 bg-white border border-ink/10 rounded-lg">
                  {s.role}
                </span>
              ))}
            </div>
          </div>
        )}
        <ObjectListEditor
          items={draft.stakeholders}
          onChange={(v) => set('stakeholders', v)}
          addLabel="Add Stakeholder"
          fields={[
            { key: 'role', label: 'Role', placeholder: 'e.g. Chair' },
            { key: 'name', label: 'Name', placeholder: 'e.g. J. Smith' },
            { key: 'input', label: 'Expected Input', placeholder: 'What perspective they provide', full: true, textarea: true },
          ]}
        />
      </SectionCard>

      <SectionCard title="Planning Notes" icon={CalendarRange}>
        <TextArea_
          rows={3}
          value={draft.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Timeline, milestones, evidence-gathering approach, constraints..."
        />
      </SectionCard>
    </div>
  );
};

export default PlanningTab;
